import { useEffect, useState } from 'react';
import { FolderKanban, User as UserIcon, Users, UsersRound, X } from 'lucide-react';
import type { AccessGrantView, FolderEditMode, FolderView, PrincipalKind, Visibility } from '@vwt/contracts';
import { Avatar, Button, Dialog, DialogContent, DialogFooter, toast } from '@vwt/ui';
import { useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { PrincipalPicker } from '../../components/common.js';
import { useFolderLabel } from './folder-label.js';
import { isPublicTreeRestricted } from './public-confirm.js';

export const DOCUMENT_PRINCIPAL_KINDS = 'USER,TEAM,GROUP,PROJECT';
const icons = { USER: UserIcon, TEAM: Users, GROUP: UsersRound, PROJECT: FolderKanban } as const;

/** Editable list of people, teams, groups and projects. */
export function PrincipalList({ value, onChange, label, kinds = DOCUMENT_PRINCIPAL_KINDS }: { value: AccessGrantView[]; onChange: (next: AccessGrantView[]) => void; label: string; kinds?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {value.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {value.map((p) => {
            const Icon = icons[p.principalKind];
            return (
              <li key={`${p.principalKind}:${p.principalId}`} className="flex items-center gap-1.5 rounded-full border border-border bg-surface-2 py-0.5 pr-1 pl-1.5 text-sm">
                {p.principalKind === 'USER' ? <Avatar name={p.principalName} size="sm" /> : <Icon className="size-3.5 text-fg-subtle" aria-hidden />}
                <span className="max-w-48 truncate">{p.principalName}</span>
                <span className="sr-only">({t(`documents.principalKind.${p.principalKind}`)})</span>
                <button type="button" className="rounded-full p-0.5 text-fg-subtle hover:bg-muted hover:text-fg" aria-label={t('documents.removePrincipal', { name: p.principalName })} onClick={() => onChange(value.filter((v) => !(v.principalKind === p.principalKind && v.principalId === p.principalId)))}>
                  <X className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <PrincipalPicker
        value={null}
        kinds={kinds}
        allowClear={false}
        label={t('documents.addPrincipal')}
        placeholder={t('documents.addPrincipal')}
        onChange={(p) => {
          if (!p || value.some((v) => v.principalKind === p.kind && v.principalId === p.id)) return;
          onChange([...value, { principalKind: p.kind as PrincipalKind, principalId: p.id, principalName: p.name }]);
        }}
      />
    </div>
  );
}

export function Choice<T extends string>({ name, value, current, onSelect, label, help, disabled = false }: { name: string; value: T; current: T; onSelect: (v: T) => void; label: string; help?: string | undefined; disabled?: boolean }) {
  const id = `${name}-${value}`;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-2 rounded-[var(--radius)] border border-border p-2.5 has-[:checked]:border-accent has-[:checked]:bg-accent-soft/40 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
    >
      <input id={id} type="radio" name={name} className="mt-0.5 accent-[var(--color-accent)]" checked={current === value} disabled={disabled} onChange={() => onSelect(value)} />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">{label}</span>
        {help ? <span className="text-xs text-fg-subtle">{help}</span> : null}
      </span>
    </label>
  );
}

/** Who views and who edits a folder (ADR-0040). Only the folder's editors open it. */
export function FolderAccessDialog({ folder, open, onOpenChange }: { folder: FolderView; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const label = useFolderLabel();
  const [visibility, setVisibility] = useState<Visibility>(folder.visibility);
  const [viewers, setViewers] = useState<AccessGrantView[]>(folder.viewers);
  const [editMode, setEditMode] = useState<FolderEditMode>(folder.editMode);
  const [editors, setEditors] = useState<AccessGrantView[]>(folder.editors);
  useEffect(() => {
    if (!open) return;
    setVisibility(folder.visibility);
    setViewers(folder.viewers);
    setEditMode(folder.editMode);
    setEditors(folder.editors.length ? folder.editors : folder.effectiveEditors.editors);
  }, [open, folder]);
  const save = useApiMutation('setDocumentFolderAccess', { invalidate: ['getDocumentFolder', 'listDocumentFolders', 'listFolderDocuments', 'getDocument'] });
  const inherited = folder.effectiveEditors.folderId !== folder.id ? folder.effectiveEditors : null;
  // In the public tree the top of the path is the "Public external" folder, named in the reader's language.
  const publicRootId = folder.publicTree ? (folder.path[0]?.id ?? folder.id) : null;
  const inheritedText = inherited
    ? inherited.mode === 'VIEWERS'
      ? t('documents.everyoneWhoViews')
      : `${t('documents.editsFrom', { folder: label({ name: inherited.folderName, systemKind: inherited.folderId === publicRootId ? 'PUBLIC_EXTERNAL' : null }) })} ${inherited.editors.map((e) => e.principalName).join(', ')}`
    : undefined;
  // Everything in the public tree is published by link, so its folders cannot restrict viewers (ADR-0056).
  const restrictLocked = folder.publicTree && folder.visibility !== 'RESTRICTED';
  const strip = (list: AccessGrantView[]) => list.map(({ principalKind, principalId }) => ({ principalKind, principalId }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('documents.accessTitle')} description={label(folder)}>
        <form
          className="flex flex-col gap-5"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await save.mutateAsync({ params: { id: folder.id }, body: { expectedVersion: folder.version, visibility, viewers: strip(viewers), editMode, editors: strip(editors) } });
              toast.success(t('documents.accessSaved'));
              onOpenChange(false);
            } catch (error) {
              if (isPublicTreeRestricted(error)) toast.error(t('documents.public.restrictedNotAllowed'));
              else toastError(error);
            }
          }}
        >
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">{t('documents.viewers')}</legend>
            <Choice name="visibility" value="COMPANY" current={visibility} onSelect={setVisibility} label={t('documents.viewersCompany')} help={t('documents.viewersCompanyHelp')} />
            <Choice
              name="visibility"
              value="RESTRICTED"
              current={visibility}
              onSelect={setVisibility}
              label={t('documents.viewersRestricted')}
              help={restrictLocked ? t('documents.public.restrictedNotAllowed') : t('documents.viewersRestrictedHelp')}
              disabled={restrictLocked}
            />
            {visibility === 'RESTRICTED' ? <PrincipalList value={viewers} onChange={setViewers} label={t('documents.viewers')} /> : null}
          </fieldset>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">{t('documents.editors')}</legend>
            {folder.parentId ? <Choice name="editMode" value="INHERIT" current={editMode} onSelect={setEditMode} label={t('documents.editInherit')} help={inheritedText} /> : null}
            <Choice name="editMode" value="VIEWERS" current={editMode} onSelect={setEditMode} label={t('documents.editViewers')} />
            <Choice name="editMode" value="LISTED" current={editMode} onSelect={setEditMode} label={t('documents.editListed')} />
            {editMode === 'LISTED' ? <PrincipalList value={editors} onChange={setEditors} label={t('documents.editors')} /> : null}
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
